"use client";

/**
 * Confirm dialog for cancelling / changing activity registrations on the public landing page.
 * Reused by two entry points:
 *  - the activity card's fast path (a single cancel, no adds), and
 *  - the manage modal's REVIEW step (a diff that removes at least one slot).
 *
 * The parent owns the reconcile + toast; this component only renders the summary and reports
 * back/confirm. Stacked on top of the signup modal when opened from there (FetchModal supports
 * stacking), so it must not assume it's the only open modal.
 */

import type React from "react";
import { AlertTriangle } from "lucide-react";
import FetchModal from "@/fetch-ui/FetchModal";
import { getButtonTextColor } from "@/features/landing-pages/_logic/color-utils";

export interface ConfirmChangesModalProps {
  /** Activity title, shown as context. */
  activityTitle: string;
  /** Formatted slot labels being registered (added). May be empty. */
  toAdd: string[];
  /** Formatted slot labels being cancelled. Always at least one (this dialog is for removals). */
  toCancel: string[];
  /** Page brand color, used for the non-destructive "Confirm changes" button. */
  primaryColor: string;
  /** True while the reconcile is in flight — disables both buttons and shows a spinner label. */
  isSubmitting: boolean;
  /** Go back to editing (or dismiss the fast-path dialog). */
  onBack: () => void;
  /** Commit the change. */
  onConfirm: () => void;
}

/**
 * Renders the cancel/change confirmation.
 *
 * @param props - See {@link ConfirmChangesModalProps}.
 * @returns The confirm modal.
 */
export function ConfirmChangesModal({
  activityTitle,
  toAdd,
  toCancel,
  primaryColor,
  isSubmitting,
  onBack,
  onConfirm,
}: ConfirmChangesModalProps): React.ReactElement {
  // Derived values
  const isCancelOnly = toAdd.length === 0;
  const isSingleCancel = isCancelOnly && toCancel.length === 1;
  const title = isSingleCancel ? "Cancel your spot?" : "Confirm your changes";
  const backLabel = isCancelOnly ? "Keep my spot" : "Back";
  const confirmLabel = isSubmitting ? "Saving..." : isCancelOnly ? "Yes, cancel" : "Confirm changes";
  const buttonTextColor = getButtonTextColor(primaryColor);

  // Render
  return (
    <FetchModal
      isOpen
      onClose={onBack}
      title={title}
      size="auto"
      // Above FetchModal's default (99999) so it stacks over the signup modal when opened from there.
      zIndex={100001}
      className="mx-4 my-8 h-auto w-full max-w-sm rounded-3xl p-6"
      contentClassName="overflow-y-auto overscroll-contain px-1"
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm font-medium text-gray-700">{activityTitle}</p>

        {isSingleCancel ? (
          <p className="text-sm font-semibold text-gray-900">{toCancel[0]}</p>
        ) : (
          <div className="flex flex-col gap-3">
            {toCancel.length > 0 && (
              <div className="flex flex-col gap-1">
                <p className="text-sm font-semibold text-gray-900">Cancelling</p>
                <ul className="flex flex-col gap-0.5">
                  {toCancel.map((label) => (
                    <li key={`cancel-${label}`} className="text-sm font-medium text-gray-600">
                      • {label}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {toAdd.length > 0 && (
              <div className="flex flex-col gap-1">
                <p className="text-sm font-semibold text-gray-900">Adding</p>
                <ul className="flex flex-col gap-0.5">
                  {toAdd.map((label) => (
                    <li key={`add-${label}`} className="text-sm font-medium text-gray-600">
                      • {label}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {toCancel.length > 0 && (
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
            <p className="text-sm font-medium text-amber-800">
              Cancelled seats are released and offered to anyone on the waitlist.
            </p>
          </div>
        )}

        <div className="mt-1 flex gap-3">
          <button
            type="button"
            onClick={onBack}
            disabled={isSubmitting}
            className="flex-1 touch-manipulation rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors [@media(hover:hover)]:hover:bg-gray-50 disabled:opacity-50"
          >
            {backLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting}
            className="flex-1 touch-manipulation rounded-lg px-4 py-2.5 text-sm font-semibold transition-opacity [@media(hover:hover)]:hover:opacity-90 disabled:opacity-50"
            style={
              isCancelOnly
                ? { backgroundColor: "#dc2626", color: "#ffffff" }
                : { backgroundColor: primaryColor, color: buttonTextColor }
            }
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </FetchModal>
  );
}
